import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Slider, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import http from '../../services/http';
import { carregarPolitica, recarregar } from '../../services/politicaDeAnexos';

/**
 * O limite de anexo da empresa, na tela de Configurações.
 *
 * ## O que ele controla
 *
 * O tamanho máximo de arquivo no anexo de chamado E no arquivo do chat -- os
 * dois passaram a ler a mesma política em 13/10/2026. Antes eram cinco tetos
 * diferentes e chumbados (25 MB no chamado, 10 no chat pelo servidor, 21 no
 * chat pelo front, 10 na Base de Conhecimento, 10 na config do websocket),
 * nenhum deles escolhido por ninguém.
 *
 * ## Por que mostra DOIS números
 *
 * Porque eles podem divergir, e a diferença é invisível de outro jeito. O
 * limite que vale é o menor entre o configurado aqui e o que o servidor web
 * aceita (`upload_max_filesize`/`post_max_size`). Quem digita 1024 sem que o
 * servidor tenha sido ajustado grava 1024, vê 128 valendo e não teria como
 * descobrir por quê -- o upload morreria antes de chegar ao PHP, com um erro
 * que não se parece com "arquivo grande demais".
 *
 * Só aparece para administrador: o limite decide quanto disco a empresa
 * consome, e o disco não tem backup automático.
 */
/**
 * As marcas da régua.
 *
 * Não são decorativas: são o que transforma a régua em grandeza. Cada uma é um
 * número com significado nesta casa -- 25 MB era o teto antigo do chamado,
 * 100 MB é o padrão do sistema hoje, e 1024 MB é o "1 GB" do pedido original.
 *
 * Sem elas o slider seria uma barra de 1 a 1024 onde nenhuma posição diz nada.
 */
const MARCAS_DO_LIMITE = [
  { value: 1, label: '1' },
  { value: 25, label: '25' },
  { value: 100, label: '100' },
  { value: 512, label: '512' },
  { value: 1024, label: '1 GB' },
];

const LimiteDeAnexo = ({ theme }) => {
  const [politica, setPolitica] = useState(null);
  const [valor, setValor] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let vivo = true;

    carregarPolitica().then((p) => {
      if (!vivo) return;

      setPolitica(p);
      if (p) setValor(String(p.configuradoMb));
    });

    return () => {
      vivo = false;
    };
  }, []);

  if (!politica || !politica.podeConfigurar) {
    return null;
  }

  const salvar = async () => {
    const numero = Number(valor);

    if (!Number.isInteger(numero) || numero < 1 || numero > 1024) {
      toast.error('Informe um número inteiro entre 1 e 1024 MB.');
      return;
    }

    setSalvando(true);

    try {
      await http.put('/politica-de-anexos', { limite_mb: numero });

      // Esquece o que estava em memória: o chat e a tela de chamado precisam
      // parar de usar o limite antigo sem exigir F5.
      const atualizada = await recarregar();

      setPolitica(atualizada);
      if (atualizada) setValor(String(atualizada.configuradoMb));

      toast.success('Limite de anexo atualizado.');
    } catch (erro) {
      toast.error(erro?.response?.data?.errors?.limite_mb?.[0] || 'Não foi possível salvar o limite.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Box sx={{ mb: 4, mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: theme.palette.text.primary }}>
        Limite de anexo
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
        Tamanho máximo de cada arquivo anexado a um chamado ou enviado no chat.
      </Typography>

      {/*
        * Slider E campo, ligados ao mesmo valor (pedido de 15/10).
        *
        * O slider sozinho não serve: ninguém acerta 100 contra 110 arrastando,
        * e este número vai para uma validação -- errar por 10 MB é um arquivo
        * recusado sem motivo aparente. O campo sozinho não dá noção de escala:
        * "1024" não diz se é muito ou pouco até alguém comparar com o resto.
        *
        * Juntos, cada um resolve o problema do outro: arrasta para a faixa,
        * digita para cravar. As marcas existem pelo mesmo motivo -- elas são o
        * que transforma a régua em grandeza (25 é o antigo teto do chamado,
        * 100 é o padrão do sistema).
        */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ width: 300, pt: 0.5 }}>
          <Slider
            value={Number(valor) >= 1 && Number(valor) <= 1024 ? Number(valor) : 1}
            onChange={(e, novo) => setValor(String(novo))}
            min={1}
            max={1024}
            step={1}
            // Escala linear, com marcas nos valores que significam algo. A
            // alternativa seria uma régua logarítmica, que daria mais curso aos
            // valores pequenos -- mas aí arrastar deixa de ser previsível, e
            // quem precisa de precisão tem o campo ao lado para isso.
            marks={MARCAS_DO_LIMITE}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v} MB`}
            aria-label="Limite por arquivo em MB"
            data-testid="slider-limite-de-anexo"
            sx={{ '& .MuiSlider-markLabel': { fontSize: '0.7rem' } }}
          />
        </Box>
        <TextField
          label="Limite por arquivo (MB)"
          type="number"
          size="small"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          inputProps={{ min: 1, max: 1024, 'data-testid': 'campo-limite-de-anexo' }}
          sx={{ width: 180 }}
        />
        <Button
          variant="contained"
          onClick={salvar}
          disabled={salvando}
          sx={{ textTransform: 'none', fontWeight: 600, mt: 0.25 }}
        >
          {salvando ? 'Salvando...' : 'Salvar limite'}
        </Button>
      </Box>

      {/*
        * O aviso diz o NÚMERO e o efeito, e não o que fazer a respeito.
        *
        * Ele trazia o nome das duas diretivas do PHP, e saiu a pedido do dono
        * do produto em 15/10: quem lê esta tela é o administrador da empresa,
        * que não mexe no servidor -- para ele aquilo era ruído que fazia a
        * mensagem parecer um erro do sistema em vez de um limite.
        *
        * Quem precisa da parte técnica é quem opera a infraestrutura, e essa
        * pessoa não descobre o problema por esta tela.
        *
        * O comentário fica ACIMA do `&&`, e não dentro do parêntese: ali ele
        * seria lido como objeto e o arquivo não compila.
        */}
      {politica.cortadoPeloServidor && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          O servidor aceita no máximo <b>{politica.tetoDoServidorMb} MB</b> por arquivo, então é
          este o limite que vale hoje — mesmo com {politica.configuradoMb} MB configurado aqui.
          Para subir além disso, fale com quem cuida do servidor.
        </Alert>
      )}

      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        Valendo agora: <b>{politica.limiteMb} MB</b> por arquivo.
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
        Tipos aceitos: {politica.tipos.join(', ')}.
      </Typography>
    </Box>
  );
};

export default LimiteDeAnexo;
